import { useCallback, useRef, useState } from 'react'

/**
 * Shared range-selection (Shift+Click) helpers for any multi-select list/table.
 *
 * The behaviour matches what users expect from Gmail / Finder / most tables:
 *   • plain click  → toggles one row and becomes the new "anchor"
 *   • Shift+click  → selects (or deselects) every row between the anchor and the
 *                    clicked row, mirroring the clicked row's resulting state.
 *
 * `rangeIdsBetween` is pure so it can be unit-tested and reused by bespoke
 * surfaces that manage their own selection state; `useRangeSelection` is a
 * ready-made hook for surfaces that don't already have one.
 */

/** Return the contiguous slice of `orderedIds` between two ids (inclusive). */
export function rangeIdsBetween(orderedIds, anchorId, targetId) {
  if (!Array.isArray(orderedIds) || orderedIds.length === 0) {
    return targetId == null ? [] : [targetId]
  }
  const a = orderedIds.indexOf(anchorId)
  const b = orderedIds.indexOf(targetId)
  // Anchor or target not on the current page → treat as a single click.
  if (a === -1 || b === -1) return targetId == null ? [] : [targetId]
  const [lo, hi] = a < b ? [a, b] : [b, a]
  return orderedIds.slice(lo, hi + 1)
}

/** Add or remove a list of ids from a Set, returning a new Set. */
export function applyRange(prevSet, ids, shouldSelect) {
  const next = new Set(prevSet)
  for (const id of ids) {
    if (shouldSelect) next.add(id)
    else next.delete(id)
  }
  return next
}

/**
 * Selection hook with built-in Shift+Click range support.
 *
 * @param {() => string[] | string[]} orderedIds  The ids in current visual
 *        order (or a getter returning them) — needed to resolve a range.
 * @returns { selected, setSelected, isSelected, toggle, clear, handleSelectClick }
 *
 * Wire `handleSelectClick(event, id)` onto the row/checkbox `onClick` and use a
 * NO-OP `onChange` (e.g. `onChange={() => {}}`). This hook owns ALL toggling —
 * both the single-row toggle and the Shift+click range — so there is no split
 * onClick/onChange that could cancel the clicked row (the "last selected row
 * not working" bug).
 */
export function useRangeSelection(orderedIds) {
  const [selected, setSelected] = useState(() => new Set())
  const anchorRef = useRef(null)

  const resolveOrdered = useCallback(
    () => (typeof orderedIds === 'function' ? orderedIds() : orderedIds) || [],
    [orderedIds],
  )

  const isSelected = useCallback((id) => selected.has(id), [selected])

  const toggle = useCallback((id) => {
    setSelected((s) => {
      const n = new Set(s)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
    anchorRef.current = id
  }, [])

  const clear = useCallback(() => {
    setSelected(new Set())
    anchorRef.current = null
  }, [])

  const handleSelectClick = useCallback(
    (event, id) => {
      const ordered = resolveOrdered()
      if (event?.shiftKey && anchorRef.current != null && ordered.length) {
        const ids = rangeIdsBetween(ordered, anchorRef.current, id)
        if (ids.length > 1) {
          // No `event.preventDefault()`: these are CONTROLLED checkboxes
          // (`checked` + no-op onChange). Preventing the default on the clicked
          // checkbox desynced React's controlled value, leaving the
          // shift-clicked row itself unchecked. Our range logic sets the
          // clicked row to exactly what the native toggle would, so it stays
          // consistent without preventDefault.
          const shouldSelect = !selected.has(id)
          setSelected((s) => applyRange(s, ids, shouldSelect))
          anchorRef.current = id
          return
        }
      }
      // Plain click (or Shift with no resolvable range): toggle just this row.
      setSelected((s) => {
        const n = new Set(s)
        n.has(id) ? n.delete(id) : n.add(id)
        return n
      })
      anchorRef.current = id
    },
    [resolveOrdered, selected],
  )

  return { selected, setSelected, isSelected, toggle, clear, anchorRef, handleSelectClick }
}
